import { useSyncExternalStore } from "react";
import { z } from "zod";

import { reportSchemaError } from "@/diagnostics/schema-error.ts";

/**
 * A set of listing ids this browser remembers — the machinery behind both
 * `listing.seen.ts` and `listing.tracked.ts`.
 *
 * Kept out of `search.state.ts` deliberately. That blob is the *view* — the
 * filters and camera you left behind — and is small, rewritten wholesale on
 * every pan. These grow and change for entirely different reasons, so mixing
 * them would mean rewriting thousands of ids every time the map moves.
 *
 * A plain array on disk rather than a Set: insertion order is what makes the cap
 * meaningful, since the oldest entries are the ones worth forgetting first.
 */

export type MarkedIds = z.infer<typeof MarkedIdsSchema>;
export const MarkedIdsSchema = z.array(z.string());

export interface IdStore {
  /** The current set. A new instance on every change and the same one otherwise,
   *  which is what `useSyncExternalStore` needs to avoid re-rendering the map on
   *  every unrelated update. */
  use: () => ReadonlySet<string>;
  add: (id: string) => void;
  toggle: (id: string) => void;
  clear: () => void;
}

export function createIdStore({
  key,
  legacyKeys = [],
  limit,
  label,
}: {
  key: string;
  /** Dropped on the way past, so a retired key doesn't sit in storage forever. */
  legacyKeys?: string[];
  limit: number;
  /** What a failed parse is called in the error report. */
  label: string;
}): IdStore {
  function load(): string[] {
    try {
      for (const stale of legacyKeys) localStorage.removeItem(stale);
      const raw = localStorage.getItem(key);
      if (!raw) return [];
      const parsed = MarkedIdsSchema.safeParse(JSON.parse(raw));
      if (!parsed.success) {
        reportSchemaError(label, parsed.error);
        return [];
      }
      return parsed.data;
    } catch (err) {
      if (localStorage.getItem(key)) reportSchemaError(label, err);
      return [];
    }
  }

  let order = load();
  let snapshot: ReadonlySet<string> = new Set(order);
  const listeners = new Set<() => void>();

  function commit(next: string[]): void {
    order = next;
    snapshot = new Set(order);
    try {
      if (order.length === 0) localStorage.removeItem(key);
      else localStorage.setItem(key, JSON.stringify(order));
    } catch {
      // Private browsing has no room for this; forgetting is the correct failure.
    }
    for (const fn of listeners) fn();
  }

  return {
    use: () =>
      useSyncExternalStore(
        (fn) => {
          listeners.add(fn);
          return () => listeners.delete(fn);
        },
        () => snapshot,
      ),
    add: (id) => {
      if (!snapshot.has(id)) commit([...order, id].slice(-limit));
    },
    toggle: (id) => {
      commit(snapshot.has(id) ? order.filter((x) => x !== id) : [...order, id].slice(-limit));
    },
    clear: () => commit([]),
  };
}
